/**
 * WebSocket bridge for live document access.
 *
 * Connects to the backend WebSocket endpoint and handles requests to
 * read/write the Word document via Office JS. The backend sends requests
 * (e.g., "read section 9.1"), this module executes them via wordWriter.ts,
 * and sends the results back.
 *
 * This is the frontend half of the document bridge. The backend half is
 * server/document_bridge.py.
 *
 * Usage:
 *   const bridge = new DocumentBridgeClient("ws://localhost:8000/api/ws/document");
 *   await bridge.connect();
 *   // bridge handles requests automatically via onmessage
 *   // use bridge.connectionId in chat requests
 *   bridge.disconnect();
 */

import {
	readProvenanceXml,
	writeProvenanceXml,
} from "./provenanceManifest.office";
import { withWordOpLog } from "./wordOpsLogger";
import {
	type GetTableOptions,
	type InsertDeleteTableColumnsOptions,
	type InsertDeleteTableColumnsResult,
	type InsertDeleteTableRowsOptions,
	type InsertDeleteTableRowsResult,
	type LiveSection,
	type ListTablesOptions,
	type MergeTableCellsOptions,
	type MergeTableCellsResult,
	type ResizeTableColumnsOptions,
	type ResizeTableColumnsResult,
	type SetTableBordersOptions,
	type SetTableBordersResult,
	type SetTableShadingOptions,
	type SetTableShadingResult,
	type SetTableCellValuesOptions,
	type SetTableCellValuesResult,
	type InsertRowsAndSetValuesResult,
	type SplitTableCellOptions,
	type SplitTableCellResult,
	type PositionMatch,
	type ReadAtAnchor,
	type ReadAtResult,
	type ReplaceTextAnchor,
	type ReplaceTextResult,
	type TableListItem,
	type TableReadResult,
	type WriteAtAnchor,
	type WriteAtResult,
	findPositions,
	getTable,
	getActiveRunId,
	insertTableColumns,
	insertTableRows,
	insertSectionContent,
	listTables,
	loadDocumentId,
	loadDocSelectionProps,
	loadWordHydrationMarker,
	loadWordInsertionMode,
	mergeTableCells,
	resizeTableColumns,
	readAllSections,
	readSectionSubtree,
	readAtPositions,
	replaceText,
	scaffoldDocument,
	setTableBorders,
	setTableCellValues,
	insertTableRowsAndSetValues,
	setTableShading,
	splitTableCell,
	deleteTableColumns,
	deleteTableRows,
	writeAtPositions,
} from "./wordWriter";

import { type TableCapabilities, probeTableCapabilities } from "./wordProbe";
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BridgeRequest {
	id: string;
	action: string;
	section_number?: string;
	content?: string;
	query?: string;
	// user_input action fields
	question?: string;
	input_type?: string;
	options?: string[];
	context?: string;
	// find_positions action fields
	section_scope?: string | null;
	anchor_types?: string[];
	limit?: number;
	// list/get table action fields
	table_match_id?: string;
	start_cell?: string;
	end_cell?: string;
	cell?: string;
	row_count?: number;
	column_count?: number;
	count?: number;
	position?: "before" | "after";
	row?: number;
	column?: number;
	border_location?:
		| "Top"
		| "Left"
		| "Bottom"
		| "Right"
		| "InsideHorizontal"
		| "InsideVertical"
		| "Inside"
		| "Outside"
		| "All";
	border_type?:
		| "None"
		| "Single"
		| "Double"
		| "Dotted"
		| "Dashed"
		| "DotDashed"
		| "Dot2Dashed";
	color?: string;
	width?: number;
	start_column?: string;
	end_column?: string;
	width_points?: number;
	values?: string[][];
	label?: string;
	include?: "schema" | "sample" | "full";
	row_window?: number;
	// read_at_positions action fields
	anchors?: ReadAtAnchor[];
	// write_at_positions action fields
	write_anchors?: WriteAtAnchor[];
	// replace_text action fields
	replace_text_anchors?: ReplaceTextAnchor[];
	// write_provenance action field — raw XML string produced by Python
	xml?: string;
}

interface BridgeResponse {
	id: string;
	[key: string]: unknown;
}

/** Request from the backend for user input — rendered as InputBlock. */
export interface UserInputRequest {
	id: string;
	question: string;
	inputType: string;
	options?: string[];
	context?: string;
}

type ConnectionCallback = (connectionId: string) => void;
type DisconnectCallback = () => void;
type UserInputCallback = (request: UserInputRequest) => void;

// ---------------------------------------------------------------------------
// DocumentBridgeClient
// ---------------------------------------------------------------------------

export class DocumentBridgeClient {
	private ws: WebSocket | null = null;
	private _connectionId: string | null = null;
	private _url: string;
	private _onConnect: ConnectionCallback | null = null;
	private _onDisconnect: DisconnectCallback | null = null;
	private _onUserInput: UserInputCallback | null = null;
	private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private _intentionalClose = false;

	constructor(url: string) {
		this._url = url;
	}

	get connectionId(): string | null {
		return this._connectionId;
	}

	get isConnected(): boolean {
		return this.ws?.readyState === WebSocket.OPEN;
	}

	onConnect(cb: ConnectionCallback): void {
		this._onConnect = cb;
	}

	onDisconnect(cb: DisconnectCallback): void {
		this._onDisconnect = cb;
	}

	/**
	 * Register callback for user input requests from the backend.
	 * When Claude calls request_user_input, the backend sends an action
	 * through the WebSocket. This callback renders the InputBlock in the UI.
	 */
	onUserInputRequest(cb: UserInputCallback): void {
		this._onUserInput = cb;
	}

	/**
	 * Send the user's response to a user_input request back to the backend.
	 * Unblocks the sync thread waiting in DocumentBridge.request_sync().
	 */
	submitUserInput(requestId: string, value: string): void {
		this._send({ id: requestId, value });
	}

	/**
	 * Connect to the backend WebSocket endpoint.
	 * Returns a promise that resolves with the connection_id.
	 */
	connect(): Promise<string> {
		return new Promise((resolve, reject) => {
			this._intentionalClose = false;

			try {
				this.ws = new WebSocket(this._url);
			} catch (err) {
				reject(new Error(`WebSocket connection failed: ${err}`));
				return;
			}

			this.ws.onopen = () => {
			};

			this.ws.onmessage = (event) => {
				let data: Record<string, unknown>;
				try {
					data = JSON.parse(event.data);
				} catch {
					console.warn("[DocumentBridge] Invalid JSON:", event.data);
					return;
				}

				// Handle initial connection message
				if (
					data.type === "connected" &&
					typeof data.connection_id === "string"
				) {
					this._connectionId = data.connection_id;
					this._onConnect?.(this._connectionId);
					resolve(this._connectionId);
					return;
				}

				// Handle document operation requests from backend
				if (data.id && data.action) {
					this._handleRequest(data as unknown as BridgeRequest);
				}
			};

			this.ws.onclose = () => {
				console.log("[DocumentBridge] WebSocket closed");
				this._connectionId = null;
				this._onDisconnect?.();

				// Auto-reconnect unless intentionally closed
				if (!this._intentionalClose) {
					this._reconnectTimer = setTimeout(() => {
						console.log("[DocumentBridge] Attempting reconnect...");
						this.connect().catch(() => {
							// Will retry again via onclose
						});
					}, 3000);
				}
			};

			this.ws.onerror = (err) => {
				console.warn("[DocumentBridge] WebSocket error:", err);
				// onclose will fire after this
			};

			// Timeout for initial connection
			setTimeout(() => {
				if (!this._connectionId) {
					reject(new Error("WebSocket connection timeout"));
				}
			}, 10000);
		});
	}

	/**
	 * Register the Word document's stable AsthrDocumentId with the backend
	 * bridge connection. Called once after connect() resolves so the backend
	 * can attach doc_id context to this WebSocket slot — enabling library /
	 * doc-type fallback resolution without an extra round-trip from every
	 * chat request.
	 */
	sendContext(docId: string): void {
		if (this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify({ type: "register_context", doc_id: docId }));
		}
	}

	/**
	 * Disconnect from the backend.
	 */
	disconnect(): void {
		this._intentionalClose = true;
		if (this._reconnectTimer) {
			clearTimeout(this._reconnectTimer);
			this._reconnectTimer = null;
		}
		if (this.ws) {
			const ws = this.ws;
			this.ws = null;
			if (ws.readyState !== WebSocket.CONNECTING) {
				ws.close();
			}
		}
		this._connectionId = null;
	}

	// ── Request handlers ──────────────────────────────────────────

	private async _handleRequest(request: BridgeRequest): Promise<void> {
		try {
			let response: BridgeResponse;

			switch (request.action) {
				case "list_sections":
					response = await this._handleListSections(request.id);
					break;

				case "read_section_subtree":
					response = await this._handleReadSectionSubtree(
						request.id,
						request.section_number || "",
					);
					break;

				case "scaffold_document":
					response = await this._handleScaffold(request.id);
					break;

				case "write_section":
					response = await this._handleWriteSection(
						request.id,
						request.section_number || "",
						request.content || "",
					);
					break;

				case "find_positions":
					response = await this._handleFindPositions(request.id, request);
					break;

				case "list_tables":
					response = await this._handleListTables(request.id, request);
					break;

				case "get_table":
					response = await this._handleGetTable(request.id, request);
					break;

				case "merge_table_cells":
					response = await this._handleMergeTableCells(request.id, request);
					break;

				case "split_table_cell":
					response = await this._handleSplitTableCell(request.id, request);
					break;

				case "insert_table_rows":
					response = await this._handleInsertTableRows(request.id, request);
					break;

				case "delete_table_rows":
					response = await this._handleDeleteTableRows(request.id, request);
					break;

				case "insert_table_columns":
					response = await this._handleInsertTableColumns(request.id, request);
					break;

				case "delete_table_columns":
					response = await this._handleDeleteTableColumns(request.id, request);
					break;

				case "set_table_borders":
					response = await this._handleSetTableBorders(request.id, request);
					break;

				case "set_table_shading":
					response = await this._handleSetTableShading(request.id, request);
					break;

				case "resize_table_columns":
					response = await this._handleResizeTableColumns(request.id, request);
					break;

				case "set_table_cell_values":
					response = await this._handleSetTableCellValues(request.id, request);
					break;

				case "insert_table_rows_and_set_values":
					response = await this._handleInsertTableRowsAndSetValues(request.id, request);
					break;

				case "read_at_positions":
					response = await this._handleReadAtPositions(request.id, request);
					break;

				case "write_at_positions":
					response = await this._handleWriteAtPositions(request.id, request);
					break;

				case "replace_text":
					response = await this._handleReplaceText(request.id, request);
					break;

				case "table_capabilities":
					response = await this._handleTableCapabilities(request.id);
					break;

				case "read_provenance":
					response = await this._handleReadProvenance(request.id);
					break;

				case "get_runtime_metadata":
					response = await this._handleGetRuntimeMetadata(request.id);
					break;

				case "write_provenance":
					response = await this._handleWriteProvenance(
						request.id,
						request.xml ?? "",
					);
					break;

				case "user_input":
					console.debug("[DocumentBridge] Received user_input request", {
						id: request.id,
						inputType: request.input_type ?? "text",
						questionPreview: (request.question ?? "").slice(0, 200),
						hasContext: Boolean(request.context),
						optionsCount: request.options?.length ?? 0,
					});
					// Don't respond immediately — emit to UI, wait for user.
					// Response sent later via submitUserInput().
					if (this._onUserInput) {
						this._onUserInput({
							id: request.id,
							question: request.question ?? "",
							inputType: request.input_type ?? "text",
							options: request.options,
							context: request.context,
						});
						return; // No auto-response
					}
					// No callback registered — send empty response
					response = { id: request.id, value: "" };
					break;

				default:
					response = {
						id: request.id,
						error: `Unknown action: ${request.action}`,
					};
			}

			this._send(response);
		} catch (err) {
			console.error(`[DocumentBridge] Error handling ${request.action}:`, err);
			this._send({
				id: request.id,
				error: `Handler error: ${err instanceof Error ? err.message : String(err)}`,
			});
		}
	}

	private async _handleScaffold(reqId: string): Promise<BridgeResponse> {
		const count = await scaffoldDocument();
		return { id: reqId, success: count > 0, headings_inserted: count };
	}

	/**
	 * Read the doc-embedded provenance manifest XML.
	 *
	 * Returns the raw XML string (or empty string when no part exists).
	 * The Python side owns parsing — see ``LiveDocumentSource.read_provenance_manifest``.
	 * Fail-soft: never errors; returns empty xml on any Office.js failure.
	 */
	private async _handleReadProvenance(reqId: string): Promise<BridgeResponse> {
		const xml = await readProvenanceXml();
		return { id: reqId, xml: xml ?? "" };
	}

	/**
	 * Return live Word custom-property metadata useful for agent tool calls.
	 * Best-effort: each field may be null when unavailable.
	 */
	private async _handleGetRuntimeMetadata(reqId: string): Promise<BridgeResponse> {
		const [docId, activeRunId, sessionContext, hydrationMarker, insertionMode] =
			await Promise.all([
				loadDocumentId().catch(() => null),
				getActiveRunId().catch(() => null),
				loadDocSelectionProps().catch(() => null),
				loadWordHydrationMarker().catch(() => null),
				loadWordInsertionMode().catch(() => null),
			]);

		return {
			id: reqId,
			metadata: {
				doc_id: docId,
				active_run_id: activeRunId,
				session_context: sessionContext,
				hydration_marker: hydrationMarker,
				insertion_mode: insertionMode,
			},
		};
	}

	/**
	 * Replace the doc-embedded provenance manifest with the supplied XML.
	 *
	 * The XML is produced by the Python encoder and pass-through written
	 * to a CustomXmlPart in the Asthra provenance namespace. The adapter
	 * deletes any existing parts in the namespace first so the document
	 * doesn't accumulate stale manifests.
	 *
	 * Returns ``{success: false}`` on any failure — the reconciler logs
	 * and retries based on this value.
	 */
	private async _handleWriteProvenance(
		reqId: string,
		xml: string,
	): Promise<BridgeResponse> {
		if (!xml) {
			return { id: reqId, success: false, error: "empty xml payload" };
		}
		const ok = await writeProvenanceXml(xml);
		return { id: reqId, success: ok };
	}

	private async _handleListSections(reqId: string): Promise<BridgeResponse> {
		const sections: LiveSection[] = await readAllSections();

		return {
			id: reqId,
			sections: sections.map((s) => ({
				number: s.number,
				heading: s.heading,
				level: s.level,
				content: s.content,
			})),
		};
	}

	private async _handleReadSectionSubtree(
		reqId: string,
		sectionNumber: string,
	): Promise<BridgeResponse> {
		if (!sectionNumber) {
			return { id: reqId, content: null, error: "Missing section_number" };
		}
		const content = await readSectionSubtree(sectionNumber);
		return { id: reqId, content };
	}

	private async _handleWriteSection(
		reqId: string,
		sectionNumber: string,
		content: string,
	): Promise<BridgeResponse> {
		if (!sectionNumber) {
			return { id: reqId, success: false, error: "Missing section_number" };
		}

		// Enable Track Changes before the write so the edit appears as a
		// tracked revision in Word. This is deterministic infrastructure —
		// the agent doesn't call it; it happens automatically for every
		// chat-initiated write. The writer sees red strikethrough (deletions)
		// and colored text (additions) and can accept/reject.
		//
		// We save the current mode and restore it after (in case the writer
		// had it off intentionally for other reasons).
		let previousTrackingMode: string | null = null;
		try {
			await withWordOpLog("documentBridge.enableTrackChanges", () =>
				Word.run(async (context) => {
					const doc = context.document;
					doc.load("changeTrackingMode");
					await context.sync();
					previousTrackingMode = doc.changeTrackingMode as string;
					if (previousTrackingMode !== "TrackAll") {
						doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
						await context.sync();
					}
				}),
			);
		} catch {
			// Track changes API may not be available in all Word versions.
			// Non-critical: the write still proceeds, just without tracking.
			console.warn("[DocumentBridge] Could not enable Track Changes");
		}

		const success = await insertSectionContent({
			sectionNumber,
			content,
		});

		// Restore previous tracking mode (don't leave it on if writer had it off)
		if (previousTrackingMode !== null && previousTrackingMode !== "TrackAll") {
			try {
				await withWordOpLog("documentBridge.restoreTrackChanges", () =>
					Word.run(async (context) => {
						context.document.changeTrackingMode =
							previousTrackingMode as Word.ChangeTrackingMode;
						await context.sync();
					}),
				);
			} catch {
				// Non-critical
			}
		}

		return { id: reqId, success };
	}

	private _send(data: BridgeResponse): void {
		if (this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(data));
		} else {
			console.warn("[DocumentBridge] Cannot send — WebSocket not open");
		}
	}

	// ── Live bridge read handlers ──────────────────────────────────────────

	private async _handleFindPositions(
		reqId: string,
		request: BridgeRequest,
	): Promise<BridgeResponse> {
		const query = (request.query ?? "").trim();
		if (!query) {
			return { id: reqId, matches: [], error: "Missing query" };
		}

		const anchorTypes = (request.anchor_types ?? [
			"heading",
			"paragraph",
			"table",
		]) as Array<"heading" | "paragraph" | "table">;
		const matches: PositionMatch[] = await findPositions({
			query,
			section_scope: request.section_scope ?? null,
			anchor_types: anchorTypes,
			limit: request.limit ?? 10,
		});
		return { id: reqId, matches };
	}

	private async _handleReadAtPositions(
		reqId: string,
		request: BridgeRequest,
	): Promise<BridgeResponse> {
		const anchors = request.anchors ?? [];
		if (anchors.length === 0) {
			return { id: reqId, items: [], error: "Missing anchors" };
		}

		const items: ReadAtResult[] = await readAtPositions(anchors);
		return { id: reqId, items };
	}

	private async _handleListTables(
		reqId: string,
		request: BridgeRequest,
	): Promise<BridgeResponse> {
		const options: ListTablesOptions = { limit: request.limit };
		const items: TableListItem[] = await listTables(options);
		return { id: reqId, items };
	}

	private async _handleGetTable(
		reqId: string,
		request: BridgeRequest,
	): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		if (!matchId) {
			return {
				id: reqId,
				item: { match_id: "", error: "Missing table_match_id" },
			};
		}

		const options: GetTableOptions = {
			match_id: matchId,
			include: request.include,
			row_window: request.row_window,
		};
		const item: TableReadResult = await getTable(options);
		const status = item.error ? `ERROR(${item.error})` : "OK";
		console.log(`[DocumentBridge] get_table ${matchId}: ${status}`);
		return { id: reqId, item };
	}

	private async _handleMergeTableCells(
		reqId: string,
		request: BridgeRequest,
	): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const startCell = (request.start_cell ?? "").trim();
		const endCell = (request.end_cell ?? "").trim();
		if (!matchId || !startCell || !endCell) {
			return {
				id: reqId,
				item: {
					match_id: matchId,
					success: false,
					error: "Missing required parameters",
				},
			};
		}

		const options: MergeTableCellsOptions = {
			match_id: matchId,
			start_cell: startCell,
			end_cell: endCell,
			label: request.label,
		};
		const item: MergeTableCellsResult = await mergeTableCells(options);
		return { id: reqId, item };
	}

	private async _handleSplitTableCell(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const cell = (request.cell ?? "").trim();
		if (!matchId || !cell || !request.row_count || !request.column_count) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}

		const options: SplitTableCellOptions = {
			match_id: matchId,
			cell,
			rows: request.row_count,
			columns: request.column_count,
		};
		const item: SplitTableCellResult = await splitTableCell(options);
		return { id: reqId, item };
	}

	private async _handleInsertTableRows(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const cell = (request.cell ?? "").trim();
		if (!matchId || !cell) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}
		const options: InsertDeleteTableRowsOptions = {
			match_id: matchId,
			cell,
			count: request.count,
			position: request.position,
		};
		const item: InsertDeleteTableRowsResult = await insertTableRows(options);
		return { id: reqId, item };
	}

	private async _handleDeleteTableRows(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const cell = (request.cell ?? "").trim();
		if (!matchId || !cell) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}
		const options: InsertDeleteTableRowsOptions = {
			match_id: matchId,
			cell,
			count: request.count,
		};
		const item: InsertDeleteTableRowsResult = await deleteTableRows(options);
		return { id: reqId, item };
	}

	private async _handleInsertTableColumns(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const cell = (request.cell ?? "").trim();
		if (!matchId || !cell) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}
		const options: InsertDeleteTableColumnsOptions = {
			match_id: matchId,
			cell,
			count: request.count,
			position: request.position,
		};
		const item: InsertDeleteTableColumnsResult = await insertTableColumns(options);
		return { id: reqId, item };
	}

	private async _handleDeleteTableColumns(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const cell = (request.cell ?? "").trim();
		if (!matchId || !cell) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}
		const options: InsertDeleteTableColumnsOptions = {
			match_id: matchId,
			cell,
			count: request.count,
		};
		const item: InsertDeleteTableColumnsResult = await deleteTableColumns(options);
		return { id: reqId, item };
	}

	private async _handleSetTableBorders(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const startCell = (request.start_cell ?? "").trim();
		const endCell = (request.end_cell ?? "").trim();
		if (!matchId || !startCell || !endCell) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}
		const options: SetTableBordersOptions = {
			match_id: matchId,
			start_cell: startCell,
			end_cell: endCell,
			border_location: request.border_location,
			border_type: request.border_type,
			color: request.color,
			width: request.width,
		};
		const item: SetTableBordersResult = await setTableBorders(options);
		return { id: reqId, item };
	}

	private async _handleSetTableShading(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const startCell = (request.start_cell ?? "").trim();
		const endCell = (request.end_cell ?? "").trim();
		const color = (request.color ?? "").trim();
		if (!matchId || !startCell || !endCell || !color) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}
		const options: SetTableShadingOptions = {
			match_id: matchId,
			start_cell: startCell,
			end_cell: endCell,
			color,
		};
		const item: SetTableShadingResult = await setTableShading(options);
		return { id: reqId, item };
	}

	private async _handleResizeTableColumns(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const startColumn = (request.start_column ?? "").trim();
		const endColumn = (request.end_column ?? "").trim();
		if (!matchId || !startColumn || !endColumn || !request.width_points) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}
		const options: ResizeTableColumnsOptions = {
			match_id: matchId,
			start_column: startColumn,
			end_column: endColumn,
			width_points: request.width_points,
		};
		const item: ResizeTableColumnsResult = await resizeTableColumns(options);
		return { id: reqId, item };
	}

	private async _handleSetTableCellValues(reqId: string, request: BridgeRequest): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const startCell = (request.start_cell ?? "").trim();
		const values = request.values;
		if (!matchId || !startCell || !Array.isArray(values) || values.length === 0) {
			return { id: reqId, item: { match_id: matchId, success: false, error: "Missing required parameters" } };
		}
		const options: SetTableCellValuesOptions = {
			match_id: matchId,
			start_cell: startCell,
			values,
		};
		const item: SetTableCellValuesResult = await setTableCellValues(options);
		return { id: reqId, item };
	}

	private async _handleInsertTableRowsAndSetValues(
		reqId: string,
		request: BridgeRequest,
	): Promise<BridgeResponse> {
		const matchId = (request.table_match_id ?? "").trim();
		const cell = (request.cell ?? "").trim();
		const values = request.values;
		if (!matchId || !cell || !Array.isArray(values) || values.length === 0) {
			return {
				id: reqId,
				item: { match_id: matchId, success: false, error: "Missing required parameters" },
			};
		}
		const item: InsertRowsAndSetValuesResult = await insertTableRowsAndSetValues({
			match_id: matchId,
			cell,
			values,
			count: request.count,
			position: request.position,
			start_cell: request.start_cell,
		});
		return { id: reqId, item };
	}

	private async _handleWriteAtPositions(
		reqId: string,
		request: BridgeRequest,
	): Promise<BridgeResponse> {
		const writeAnchors = request.write_anchors ?? [];
		if (writeAnchors.length === 0) {
			return { id: reqId, items: [], error: "Missing write_anchors" };
		}

		let previousTrackingMode: string | null = null;
		try {
			await withWordOpLog("documentBridge.enableTrackChanges", () =>
				Word.run(async (context) => {
					const doc = context.document;
					doc.load("changeTrackingMode");
					await context.sync();
					previousTrackingMode = doc.changeTrackingMode as string;
					if (previousTrackingMode !== "TrackAll") {
						doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
						await context.sync();
					}
				}),
			);
		} catch {
			console.warn("[DocumentBridge] Could not enable Track Changes");
		}

		const items: WriteAtResult[] = await writeAtPositions(writeAnchors);

		if (previousTrackingMode !== null && previousTrackingMode !== "TrackAll") {
			try {
				await withWordOpLog("documentBridge.restoreTrackChanges", () =>
					Word.run(async (context) => {
						context.document.changeTrackingMode =
							previousTrackingMode as Word.ChangeTrackingMode;
						await context.sync();
					}),
				);
			} catch {
				// Non-critical
			}
		}
		return { id: reqId, items };
	}

	private async _handleReplaceText(
		reqId: string,
		request: BridgeRequest,
	): Promise<BridgeResponse> {
		const replaceAnchors = request.replace_text_anchors ?? [];
		if (replaceAnchors.length === 0) {
			return { id: reqId, items: [], error: "Missing replace_text_anchors" };
		}

		let previousTrackingMode: string | null = null;
		try {
			await withWordOpLog("documentBridge.enableTrackChanges", () =>
				Word.run(async (context) => {
					const doc = context.document;
					doc.load("changeTrackingMode");
					await context.sync();
					previousTrackingMode = doc.changeTrackingMode as string;
					if (previousTrackingMode !== "TrackAll") {
						doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
						await context.sync();
					}
				}),
			);
		} catch {
			console.warn("[DocumentBridge] Could not enable Track Changes");
		}

		const items: ReplaceTextResult[] = await replaceText(replaceAnchors);

		if (previousTrackingMode !== null && previousTrackingMode !== "TrackAll") {
			try {
				await withWordOpLog("documentBridge.restoreTrackChanges", () =>
					Word.run(async (context) => {
						context.document.changeTrackingMode =
							previousTrackingMode as Word.ChangeTrackingMode;
						await context.sync();
					}),
				);
			} catch {
				// Non-critical
			}
		}
		return { id: reqId, items };
	}

	private async _handleTableCapabilities(
		reqId: string,
	): Promise<BridgeResponse> {
		const capabilities: TableCapabilities = await probeTableCapabilities();
		return { id: reqId, capabilities };
	}
}
